/**
 * 用户增长中心 — 整合 5 大功能
 *
 * Tabs:
 *   - 顶级用户(跟单大 V)
 *   - 关注 Feed
 *   - 实战记录
 *   - 我的勋章
 *   - 消息中心
 */
import { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Trophy, Users, BookOpen, Award, Bell, Plus, Star, Crown,
  TrendingUp, Target, ChevronRight, Heart, CheckCircle2, XCircle,
  Send, Loader2, Sparkles, Flame
} from 'lucide-react';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { useUser } from '../stores/user.js';
import * as api from '../lib/api.js';
import { useEngine } from '../hooks/useEngine.js';
import PageHeader from '../components/PageHeader.jsx';

export default function SocialHubPage() {
  const { user } = useUser();
  const [tab, setTab] = useState('leaderboard');
  const [leaderboard, setLeaderboard] = useState([]);
  const [feed, setFeed] = useState([]);
  const [records, setRecords] = useState({ list: [], stats: {} });
  const [badges, setBadges] = useState({ earned: [], all: {} });
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (tab === 'leaderboard') loadLeaderboard();
    if (tab === 'feed' && user) loadFeed();
    if (tab === 'records' && user) loadRecords();
    if (tab === 'badges' && user) loadBadges();
    if (tab === 'messages' && user) loadMessages();
  }, [tab]);

  const loadLeaderboard = async () => {
    setLoading(true);
    try {
      const r = await api.socialLeaderboard();
      if (r?.ok) setLeaderboard(r.leaderboard || []);
    } catch (e) {}
    setLoading(false);
  };

  const loadFeed = async () => {
    setLoading(true);
    try {
      const r = await api.socialFeed();
      if (r?.ok) setFeed(r.feed || []);
    } catch (e) {}
    setLoading(false);
  };

  const loadRecords = async () => {
    setLoading(true);
    try {
      const r = await api.recordsList();
      if (r?.ok) setRecords({ list: r.records || [], stats: r.stats || {} });
    } catch (e) {}
    setLoading(false);
  };

  const loadBadges = async () => {
    setLoading(true);
    try {
      const r = await api.myBadges();
      if (r?.ok) {
        setBadges({ earned: r.earned || [], all: r.all || {} });
        // 后台检查新勋章
        api.checkBadges().then(res => {
          if (res?.ok && res.new_badges?.length) {
            toast.success(`🎉 获得 ${res.new_badges.length} 个新勋章!`);
            loadBadges();
          }
        }).catch(() => {});
      }
    } catch (e) {}
    setLoading(false);
  };

  const loadMessages = async () => {
    setLoading(true);
    try {
      const r = await api.pushList();
      if (r?.ok) setMessages(r.messages || []);
    } catch (e) {}
    setLoading(false);
  };

  return (
    <div className="space-y-3 pb-24">
      <PageHeader
        title="用户增长中心"
        subtitle="每日推送 · 跟单大 V · 实战记录 · 勋章 · 消息"
        icon={Sparkles}
      />

      <div className="card !p-2">
        <div className="grid grid-cols-5 gap-1">
          {[
            { id: 'leaderboard', label: '排行', icon: Trophy },
            { id: 'feed', label: '关注', icon: Heart },
            { id: 'records', label: '实战', icon: Target },
            { id: 'badges', label: '勋章', icon: Award },
            { id: 'messages', label: '消息', icon: Bell },
          ].map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={clsx(
                'rounded-lg py-2 text-xs font-semibold flex flex-col items-center gap-0.5 transition',
                tab === t.id
                  ? 'bg-brand-cyan/20 text-brand-cyan border border-brand-cyan/30'
                  : 'text-ink-2 hover:bg-white/5'
              )}
            >
              <t.icon size={14} />
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <AnimatePresence mode="wait">
        {tab === 'leaderboard' && <LeaderboardTab key="lb" data={leaderboard} loading={loading} user={user} />}
        {tab === 'feed' && <FeedTab key="fd" data={feed} loading={loading} user={user} />}
        {tab === 'records' && <RecordsTab key="rc" data={records} loading={loading} user={user} reload={loadRecords} />}
        {tab === 'badges' && <BadgesTab key="bg" data={badges} loading={loading} user={user} />}
        {tab === 'messages' && <MessagesTab key="mg" data={messages} loading={loading} user={user} reload={loadMessages} />}
      </AnimatePresence>
    </div>
  );
}

// ── 1. 顶级用户排行 ──
function LeaderboardTab({ data, loading, user }) {
  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} className="space-y-2">
      {loading ? (
        <div className="card text-center py-8 text-ink-2 text-sm"><Loader2 size={20} className="mx-auto mb-2 animate-spin" />加载中...</div>
      ) : data.length === 0 ? (
        <div className="card text-center py-8 text-ink-3 text-sm">
          <Trophy size={32} className="mx-auto mb-2 opacity-30" />
          还没有公开推荐的大 V
          <div className="text-xs mt-1">付费用户可发布公开推荐</div>
        </div>
      ) : (
        data.map((u, i) => (
          <div key={u.id} className={clsx(
            'card !p-3 flex items-center gap-3',
            i === 0 && 'border-brand-gold/40 bg-gradient-to-r from-brand-gold/5 to-transparent',
            i === 1 && 'border-brand-cyan/30',
            i === 2 && 'border-brand-purple/30'
          )}>
            <div className={clsx(
              'w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0',
              i === 0 ? 'bg-gradient-to-br from-brand-gold to-amber-500 text-bg-base' :
              i === 1 ? 'bg-gradient-to-br from-brand-cyan to-blue-500 text-white' :
              i === 2 ? 'bg-gradient-to-br from-brand-purple to-pink-500 text-white' :
              'bg-white/10 text-ink-2'
            )}>
              {i + 1}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-bold truncate flex items-center gap-1">
                {u.name || `用户${(u.phone || '').slice(-4)}`}
                {i === 0 && <Crown size={12} className="text-brand-gold" />}
              </div>
              <div className="text-[10px] text-ink-3">
                {u.tier || '试用'} · {u.followers || 0} 粉丝 · {u.pick_count || 0} 期
              </div>
            </div>
            <div className="text-right flex-shrink-0">
              <div className={clsx('text-base font-bold', i < 3 ? 'text-brand-gold' : 'text-ink-1')}>
                {((u.hit_rate || 0) * 100).toFixed(0)}%
              </div>
              <div className="text-[10px] text-ink-3">命中率</div>
            </div>
            <FollowButton targetUserId={u.id} />
          </div>
        ))
      )}
      <div className="rounded-lg bg-amber-500/5 border border-amber-500/20 p-2.5 text-xs text-amber-300">
        ⚠ 命中率基于公开推荐数据,实战中彩票为真随机,不可作为盈利保证
      </div>
    </motion.div>
  );
}

function FollowButton({ targetUserId }) {
  const [following, setFollowing] = useState(false);
  const [loading, setLoading] = useState(false);
  const handleClick = async () => {
    setLoading(true);
    try {
      const r = await api.socialFollow(targetUserId, following ? 'unfollow' : null);
      if (r?.ok) {
        setFollowing(r.following);
        toast.success(r.following ? '已关注' : '已取消');
      }
    } catch (e) { toast.error(e.message); }
    setLoading(false);
  };
  return (
    <button
      onClick={handleClick}
      disabled={loading}
      className={clsx(
        'text-xs px-2.5 py-1 rounded-lg flex-shrink-0',
        following ? 'bg-white/5 text-ink-2' : 'btn-primary'
      )}
    >
      {following ? '已关注' : '关注'}
    </button>
  );
}

// ── 2. 关注 Feed ──
function FeedTab({ data, loading, user }) {
  if (!user) return <div className="card text-center py-6 text-ink-3 text-sm">登录后查看</div>;
  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} className="space-y-2">
      {loading ? (
        <div className="card text-center py-8 text-ink-2 text-sm"><Loader2 size={20} className="mx-auto mb-2 animate-spin" />加载中...</div>
      ) : data.length === 0 ? (
        <div className="card text-center py-8 text-ink-3 text-sm">
          <Heart size={32} className="mx-auto mb-2 opacity-30" />
          还没有关注的人
          <div className="text-xs mt-1">去"排行" tab 关注顶级大 V</div>
        </div>
      ) : (
        data.map(p => {
          let picks = [];
          try { picks = JSON.parse(p.picks); } catch {}
          return (
            <div key={p.id} className="card !p-3">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-brand-cyan to-brand-purple flex items-center justify-center text-white text-[10px] font-bold">
                  {(p.name || p.phone || '?').slice(-2)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-bold truncate">{p.name || `用户${(p.phone || '').slice(-4)}`}</div>
                  <div className="text-[10px] text-ink-3">第 {p.period} 期 · 池子 {p.pool_size} 注</div>
                </div>
                {p.hit === 1 && <span className="text-[10px] px-1.5 py-0.5 rounded bg-brand-gold/20 text-brand-gold">✓ 命中</span>}
                {p.hit === 0 && <span className="text-[10px] px-1.5 py-0.5 rounded bg-ink-3/10 text-ink-3">未中</span>}
              </div>
              <div className="text-[10px] text-ink-2 line-clamp-2 font-mono">
                {picks.slice(0, 20).join(' ')}{picks.length > 20 ? ` ... +${picks.length - 20}` : ''}
              </div>
            </div>
          );
        })
      )}
    </motion.div>
  );
}

// ── 3. 实战记录 ──
function RecordsTab({ data, loading, user, reload }) {
  const [adding, setAdding] = useState(false);
  const [period, setPeriod] = useState('');
  const [picks, setPicks] = useState('');
  const [strategy, setStrategy] = useState('');
  const [note, setNote] = useState('');

  if (!user) return <div className="card text-center py-6 text-ink-3 text-sm">登录后查看</div>;

  const handleAdd = async () => {
    const pickArr = picks.split(/[\s,]+/).filter(x => /^\d{3}$/.test(x));
    if (!period || pickArr.length === 0) {
      toast.error('请填期号和选号(3 位数字,空格或逗号分隔)');
      return;
    }
    try {
      const r = await api.recordsAdd({
        period,
        my_picks: pickArr,
        pool_size: pickArr.length,
        strategy,
        note,
      });
      if (r?.ok) {
        toast.success(r.hit ? '✓ 命中!' : '已记录');
        setAdding(false);
        setPeriod(''); setPicks(''); setStrategy(''); setNote('');
        reload();
      }
    } catch (e) { toast.error(e.message); }
  };

  const handleReconcile = async () => {
    try {
      const r = await api.recordsReconcile();
      if (r?.ok) toast.success(`已重算 ${r.updated} 条`);
      reload();
    } catch (e) { toast.error(e.message); }
  };

  const stats = data.stats || {};
  const hitRate = ((stats.hit_rate || 0) * 100).toFixed(1);
  const profit = (stats.profit || 0);

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} className="space-y-3">
      {/* 统计卡 */}
      <div className="card">
        <h3 className="text-sm font-bold mb-2 flex items-center gap-2">
          <Target size={14} className="text-brand-cyan" />
          实战统计
        </h3>
        <div className="grid grid-cols-4 gap-2 text-center">
          <div className="rounded-lg bg-white/5 p-2">
            <div className="text-[10px] text-ink-3">总期数</div>
            <div className="text-base font-bold">{stats.total || 0}</div>
          </div>
          <div className="rounded-lg bg-white/5 p-2">
            <div className="text-[10px] text-ink-3">命中率</div>
            <div className={clsx('text-base font-bold', (stats.hit_rate || 0) >= 0.5 ? 'text-brand-gold' : 'text-brand-cyan')}>{hitRate}%</div>
          </div>
          <div className="rounded-lg bg-white/5 p-2">
            <div className="text-[10px] text-ink-3">总投入</div>
            <div className="text-base font-bold">¥{stats.total_cost?.toFixed(0) || 0}</div>
          </div>
          <div className="rounded-lg bg-white/5 p-2">
            <div className="text-[10px] text-ink-3">净收益</div>
            <div className={clsx('text-base font-bold', profit >= 0 ? 'text-brand-gold' : 'text-accent-danger')}>
              {profit >= 0 ? '+' : ''}¥{profit.toFixed(0)}
            </div>
          </div>
        </div>
        <button onClick={handleReconcile} className="text-[10px] text-brand-cyan mt-2">🔄 重新对比开奖</button>
      </div>

      <button
        onClick={() => setAdding(true)}
        className="card w-full text-center py-3 hover:border-brand-cyan/40 transition flex items-center justify-center gap-2"
      >
        <Plus size={16} className="text-brand-cyan" />
        <span className="text-sm font-semibold text-brand-cyan">添加实战记录</span>
      </button>

      {data.list.map(r => {
        let picks = [];
        try { picks = JSON.parse(r.my_picks); } catch {}
        return (
          <div key={r.id} className="card !p-3">
            <div className="flex items-center gap-2 mb-1.5">
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/5 text-ink-2">第 {r.period} 期</span>
              {r.hit === 1 && <span className="text-[10px] px-1.5 py-0.5 rounded bg-brand-gold/20 text-brand-gold font-semibold">✓ 命中</span>}
              {r.hit === 0 && <span className="text-[10px] px-1.5 py-0.5 rounded bg-ink-3/10 text-ink-3">未中</span>}
              {r.hit === null && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-400">待开奖</span>}
              {r.strategy && <span className="text-[10px] px-1.5 py-0.5 rounded bg-brand-purple/20 text-brand-purple">{r.strategy}</span>}
            </div>
            <div className="text-[10px] text-ink-2 font-mono line-clamp-2 mb-1">
              {picks.slice(0, 15).join(' ')}{picks.length > 15 ? `...+${picks.length - 15}` : ''}
            </div>
            <div className="flex items-center justify-between text-[10px] text-ink-3">
              <span>投入 ¥{r.cost?.toFixed(0) || 0} · 中奖 ¥{r.win?.toFixed(0) || 0}</span>
              {r.hit !== null && (
                <span className={clsx('font-bold', (r.win - r.cost) >= 0 ? 'text-brand-gold' : 'text-accent-danger')}>
                  {(r.win - r.cost) >= 0 ? '+' : ''}¥{(r.win - r.cost).toFixed(0)}
                </span>
              )}
            </div>
          </div>
        );
      })}

      {/* 添加弹窗 */}
      <AnimatePresence>
        {adding && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setAdding(false)}>
            <motion.div initial={{ y: 30 }} animate={{ y: 0 }} onClick={e => e.stopPropagation()} className="w-full max-w-md glass-strong rounded-2xl p-5">
              <h3 className="text-base font-bold mb-3">添加实战记录</h3>
              <div className="space-y-2.5">
                <div>
                  <label className="text-xs text-ink-2 mb-1 block">期号</label>
                  <input type="text" value={period} onChange={e => setPeriod(e.target.value)} placeholder="2026201" className="w-full bg-bg-card border border-white/10 rounded-lg px-3 py-2 text-sm outline-none" />
                </div>
                <div>
                  <label className="text-xs text-ink-2 mb-1 block">选号(空格/逗号分隔)</label>
                  <textarea value={picks} onChange={e => setPicks(e.target.value)} placeholder="123 456 789" rows={3} className="w-full bg-bg-card border border-white/10 rounded-lg px-3 py-2 text-sm outline-none font-mono resize-none" />
                </div>
                <div>
                  <label className="text-xs text-ink-2 mb-1 block">策略(可选)</label>
                  <input type="text" value={strategy} onChange={e => setStrategy(e.target.value)} placeholder="L2 / 杀号 / 凯利" className="w-full bg-bg-card border border-white/10 rounded-lg px-3 py-2 text-sm outline-none" />
                </div>
                <div className="grid grid-cols-2 gap-2 pt-1">
                  <button onClick={() => setAdding(false)} className="btn-secondary">取消</button>
                  <button onClick={handleAdd} className="btn-primary">添加</button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

// ── 4. 勋章 ──
function BadgesTab({ data, loading, user }) {
  if (!user) return <div className="card text-center py-6 text-ink-3 text-sm">登录后查看</div>;
  const earnedKeys = new Set(data.earned?.map(b => b.badge_key) || []);
  const allBadges = data.all || {};
  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} className="space-y-2">
      <div className="card">
        <h3 className="text-sm font-bold mb-2 flex items-center gap-2">
          <Award size={14} className="text-brand-gold" />
          勋章墙({earnedKeys.size}/{Object.keys(allBadges).length})
        </h3>
        <div className="grid grid-cols-3 gap-2">
          {Object.entries(allBadges).map(([key, b]) => {
            const got = earnedKeys.has(key);
            return (
              <div key={key} className={clsx(
                'rounded-xl p-2.5 text-center transition',
                got
                  ? 'bg-gradient-to-br from-brand-gold/20 to-brand-cyan/10 border border-brand-gold/30'
                  : 'bg-white/5 opacity-50'
              )}>
                <div className="text-3xl mb-1" style={{ filter: got ? '' : 'grayscale(1)' }}>{b.icon}</div>
                <div className={clsx('text-xs font-bold', got ? 'text-ink-0' : 'text-ink-3')}>{b.name}</div>
                <div className="text-[10px] text-ink-3 mt-0.5 line-clamp-1">{b.desc}</div>
                {got && <div className="text-[10px] text-brand-gold mt-0.5">✓ 已获得</div>}
              </div>
            );
          })}
        </div>
      </div>
    </motion.div>
  );
}

// ── 5. 消息中心 ──
function MessagesTab({ data, loading, user, reload }) {
  if (!user) return <div className="card text-center py-6 text-ink-3 text-sm">登录后查看</div>;

  const handleRead = async (id) => {
    try { await api.pushRead(id); reload(); } catch {}
  };

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} className="space-y-2">
      {loading ? (
        <div className="card text-center py-8 text-ink-2 text-sm"><Loader2 size={20} className="mx-auto mb-2 animate-spin" />加载中...</div>
      ) : data.length === 0 ? (
        <div className="card text-center py-8 text-ink-3 text-sm">
          <Bell size={32} className="mx-auto mb-2 opacity-30" />
          还没有消息
          <div className="text-xs mt-1">每日 9:00 推荐 / 22:00 复盘</div>
        </div>
      ) : (
        data.map(m => (
          <button
            key={m.id}
            onClick={() => !m.is_read && handleRead(m.id)}
            className={clsx(
              'card w-full !p-3 text-left transition',
              !m.is_read && 'border-brand-cyan/40 bg-brand-cyan/5'
            )}
          >
            <div className="flex items-start gap-2">
              <div className={clsx(
                'w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0',
                m.type === 'daily_recommend' ? 'bg-gradient-to-br from-brand-gold to-amber-500' :
                m.type === 'daily_recap' ? 'bg-gradient-to-br from-brand-cyan to-brand-purple' :
                'bg-white/10'
              )}>
                {m.type === 'daily_recommend' ? <Flame size={16} className="text-white" /> :
                 m.type === 'daily_recap' ? <Sparkles size={16} className="text-white" /> :
                 <Bell size={16} className="text-ink-2" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <span className="text-sm font-bold flex-1 truncate">{m.title}</span>
                  {!m.is_read && <span className="w-2 h-2 rounded-full bg-brand-cyan flex-shrink-0" />}
                </div>
                <div className="text-xs text-ink-2 mt-1 whitespace-pre-wrap">{m.content}</div>
                <div className="text-[10px] text-ink-3 mt-1.5">{fmtTime(m.sent_at)}</div>
              </div>
            </div>
          </button>
        ))
      )}
    </motion.div>
  );
}

function fmtTime(ts) {
  if (!ts) return '';
  const d = new Date(typeof ts === 'number' && ts < 1e12 ? ts * 1000 : ts);
  const now = new Date();
  const diff = now - d;
  if (diff < 3600000) return `${Math.floor(diff / 60000)} 分钟前`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)} 小时前`;
  if (diff < 7 * 86400000) return `${Math.floor(diff / 86400000)} 天前`;
  return d.toLocaleDateString('zh-CN', { month: '2-digit', day: '2-digit' });
}
